<template>
  <div>
    <div class="comment" @click="modalOpened = true">
      <b-icon icon="comment-alt" size="is-small" type="is-primary"></b-icon>
    </div>
    <b-modal
      v-model="modalOpened"
      has-modal-card
      trap-focus
      scroll="keep"
      aria-role="dialog"
      aria-label="CommentModale"
      aria-modal
      class="CommentModale"
      @cancel="modalOpened = false"
    >
      <div class="modal-card">
        <header class="modal-card-head modal-card-head-commentaires">
          <div class="commentaires">
            {{ $t("instructionPage.labellisations.harmonisation.commentaires.title") }} <br />
            <div class="commentaires-line" />
          </div>
          <div class="commentaires-author">
            <span v-if="isFinal">{{
              $t("instructionPage.labellisations.harmonisation.note.final-eval")
            }}</span>
            <span v-else>
              {{ evaluation.instructeur.prenom }}
              {{ evaluation.instructeur.nom }}
            </span>
          </div>
        </header>
        <section class="modal-card-body modal-card-body-commentaires">
          <table class="commentaires-table">
            <thead>
              <tr>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="criteria-cell">
                  {{ $t("instructionPage.labellisations.harmonisation.note.deroule") }}
                </td>
                <td class="note-popup-cell">{{ evaluation.derouleNote }}</td>
                <td class="comment-popup-cell">
                  <textarea v-model="derouleCommentaire" :readonly="!isFinal || readOnly" />
                </td>
              </tr>
              <tr>
                <td class="criteria-cell">
                  {{ $t("instructionPage.labellisations.harmonisation.note.diffusion") }}
                </td>
                <td class="note-popup-cell">{{ evaluation.adhesionNote }}</td>
                <td class="comment-popup-cell">
                  <textarea v-model="adhesionCommentaire" :readonly="!isFinal || readOnly" />
                </td>
              </tr>
              <tr>
                <td class="criteria-cell">
                  {{ $t("instructionPage.labellisations.harmonisation.note.enseignements") }}
                </td>
                <td class="note-popup-cell">{{ evaluation.integrationNote }}</td>
                <td class="comment-popup-cell">
                  <textarea v-model="integrationCommentaire" :readonly="!isFinal || readOnly" />
                </td>
              </tr>
              <tr>
                <td class="criteria-cell">
                  {{ $t("instructionPage.labellisations.harmonisation.note.implication") }}
                </td>
                <td class="note-popup-cell">{{ evaluation.implicationNote }}</td>
                <td class="comment-popup-cell">
                  <textarea v-model="implicationCommentaire" :readonly="!isFinal || readOnly" />
                </td>
              </tr>
              <tr class="final-comment">
                <td class="criteria-cell" colspan="2">
                  {{ $t("instruction-panel.instruction.commentaireInstructeur") }}
                </td>
                <td class="comment-popup-cell">
                  <textarea v-model="commentaireInstructeur" :readonly="!isFinal || readOnly" />
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="modalOpened = false">
              {{ $t("close-generic") }}
            </div>
            <b-button
              v-if="isFinal && !readOnly"
              rounded
              type="is-primary"
              class="send-final-eval"
              @click="sendFinalEval"
            >
              {{ $t("valider") }}
            </b-button>
          </div>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { EvaluationDTO, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { BButton, BIcon, BModal } from "buefy"

export default defineComponent({
  name: "LabellisationEvaluationCommentsPopup",
  components: {
    BModal,
    BButton,
    BIcon,
  },
  props: {
    projectAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    evaluation: {
      type: Object as PropType<EvaluationDTO>,
      required: true,
    },
    isFinal: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["evaluation-updated", "comment-changed"],
  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      instructionService: InstructionService.INSTANCE,
      modalOpened: false,
      evaluationCopy: { ...this.evaluation },
    }
  },
  computed: {
    derouleCommentaire: {
      get(): string {
        return this.evaluationCopy.derouleCommentaire
      },
      set(value: string) {
        this.evaluationCopy.derouleCommentaire = value
      },
    },
    adhesionCommentaire: {
      get(): string {
        return this.evaluationCopy.adhesionCommentaire
      },
      set(value: string) {
        this.evaluationCopy.adhesionCommentaire = value
      },
    },
    integrationCommentaire: {
      get(): string {
        return this.evaluationCopy.integrationCommentaire
      },
      set(value: string) {
        this.evaluationCopy.integrationCommentaire = value
      },
    },
    implicationCommentaire: {
      get(): string {
        return this.evaluationCopy.implicationCommentaire
      },
      set(value: string) {
        this.evaluationCopy.implicationCommentaire = value
      },
    },
    commentaireInstructeur: {
      get(): string {
        return this.evaluationCopy.commentaireInstructeur
      },
      set(value: string) {
        this.evaluationCopy.commentaireInstructeur = value
      },
    },
  },
  watch: {
    evaluation: {
      handler(newEvaluation) {
        this.evaluationCopy = { ...newEvaluation }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async sendFinalEval(): Promise<void> {
      if (this.projectAnnuel.id != null) {
        try {
          await this.instructionService.saveEvaluationFinale(
            this.projectAnnuel.id,
            this.evaluationCopy,
          )
          this.infoReportingService.success(
            this.$t(
              "instructionPage.labellisations.harmonisation.evaluation-finale-saved",
            ).toString(),
          )
          this.modalOpened = false
          this.$emit("comment-changed")
          this.$emit("evaluation-updated", this.evaluationCopy)
        } catch (e: any) {
          this.infoReportingService.error(
            this.$t(
              "instructionPage.labellisations.harmonisation.evaluation-finale-save-ko",
            ).toString(),
            new Error(),
          )
        }
      }
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.comment {
  cursor: pointer;
  color: $primary;
}

.CommentModale {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 100%;
  }
}

.modal-card-head-commentaires {
  display: flex;
  background-color: white !important;

  .commentaires {
    color: $ofb-bleu-2;
    font-family: $font-family-title;
    font-size: 26px;
    padding-right: 15px;
  }

  .commentaires-line {
    width: 70px;
    height: 3px;
    background-color: $ofb-bleu-2;
  }

  .commentaires-author {
    font-size: 18px;
  }
}

.modal-card-body-commentaires {
  background-color: $comments-table-background !important;
  min-height: 500px;

  .commentaires-table {
    width: 100%;
    border: 0 solid black !important;

    tr {
      border: 0 solid black !important;

      td {
        border: 0 solid black !important;

        &.criteria-cell {
          font-family: $font-family;
          width: 60px;
          text-align: right;
          padding-top: 20px;
          font-size: 15px !important;
        }

        &.note-popup-cell {
          font-size: 15px !important;
          font-family: $font-family-title;
          width: 30px;
          padding-top: 20px;
        }

        &.comment-popup-cell {
          width: 100%;

          textarea {
            font-family: $font-family !important;
            font-size: 15px !important;
            text-align: left;
            border: 1px solid $bookmarkcard-border-color !important;
            background-color: white;
            margin: 5px;
            padding: 5px 10px;
            width: 100%;
            min-height: 50px;
            border-radius: 10px;
            resize: none;
          }
        }
      }

      &.final-comment {
        background-color: $comments-table-background-final-comment;

        td {
          &.comment-popup-cell {
            p {
              min-height: 80px;
            }
          }
        }
      }
    }
  }
}

.send-final-eval {
  font-size: 15px !important;
}

.modal-card-body {
  overflow: auto !important;
}
</style>
